import React from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import moment from 'moment';
import styled from 'styled-components';
import { Label } from 'semantic-ui-react';
import AdminOverview from '../../component/AdminOverview';
import { User, UserStore } from '../../store/User';
import { ProjectStore } from '../../store/Project';
import { TicketStore } from '../../store/Ticket';
import { TeamStore } from '../../store/Team';
import { TimeRegistration, TimeRegistrationStore } from '../../store/TimeRegistration';
import { FilterTargetSmartDatePicker } from 'component/TargetSmartDateRange';
import { TicketLabel } from 'component/TimeRegistration/Form';
import { t } from '../../i18n';

const C = {
    bg: '#f5f6f7', surface: '#ffffff', border: '#e0e3e8', borderStrong: '#c5cad2',
    textPrimary: '#1a1d23', textSecondary: '#5c6470', textMuted: '#9099a8',
    accent: '#2185d0', accentLight: '#e8f3fb',
    success: '#21ba45', successLight: '#eafbef',
    warning: '#f0a721', warningLight: '#fef5e7',
    danger: '#db2828', weekend: '#f0f2f5',
    todayLine: '#2185d0', sidebarBg: '#f8f9fa',
};

const PALETTE = ['#e53e3e', '#38a169', '#3182ce', '#805ad5', '#dd6b20', '#319795', '#d53f8c', '#6b46c1'];

function colorFor(s: string): string {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return PALETTE[Math.abs(h) % PALETTE.length];
}

const DAY_WIDTH = 112;
const INITIAL_WEEKS = 3;
const ROW_LABEL_W = 220;
const PX_PER_HOUR = 10;
const MAX_ROW_HEIGHT = 240;

function getStartOfWeek(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff); d.setHours(0, 0, 0, 0);
    return d;
}
function addDays(date: Date, days: number): Date {
    const d = new Date(date); d.setDate(d.getDate() + days); return d;
}
function daysBetween(d1: Date, d2: Date): number {
    const a = new Date(d1); a.setHours(0,0,0,0);
    const b = new Date(d2); b.setHours(0,0,0,0);
    return Math.round((b.getTime() - a.getTime()) / (1000*60*60*24));
}
function isToday(date: Date): boolean {
    return new Date(date).toDateString() === new Date().toDateString();
}
function isWeekend(date: Date): boolean {
    const d = new Date(date).getDay();
    return d === 0 || d === 6;
}
function formatHours(seconds: number): string {
    const h = seconds / 3600;
    if (h < 1) return Math.round(h * 60) + 'm';
    return h.toFixed(1) + 'h';
}

function parseDateSafe(val: any): Date | null {
    if (!val) return null;
    if (val instanceof Date) return val;
    if (moment.isMoment(val)) return val.toDate();
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
}

// ── Styled components ──
const Wrapper = styled.div`
    display: flex; flex-direction: column; flex: 1; height: 100%; overflow: hidden; background: ${C.bg};
`;
const Toolbar = styled.div`
    display: flex; align-items: center; gap: 8px; padding: 8px 16px;
    border-bottom: 1px solid ${C.border}; background: ${C.surface}; flex-shrink: 0; flex-wrap: wrap;
`;
const TBtn = styled.button`
    padding: 4px 12px; border: 1px solid ${C.borderStrong}; border-radius: 4px;
    background: ${C.surface}; color: ${C.textPrimary}; cursor: pointer; font-size: 13px;
    &:hover { background: ${C.bg}; }
`;
const TBtnActive = styled(TBtn)`
    background: ${C.accent}; color: #fff; border-color: ${C.accent};
    &:hover { background: ${C.accent}; opacity: 0.9; }
`;
const WeekLabel = styled.span`
    font-size: 13px; font-weight: 600; color: ${C.textPrimary}; min-width: 200px; text-align: center;
`;
const LegendBar = styled.div`
    display: flex; align-items: center; gap: 12px; padding: 4px 16px;
    border-bottom: 1px solid ${C.border}; background: ${C.surface}; font-size: 11px; color: ${C.textSecondary}; flex-shrink: 0;
`;
const LegendItem = styled.span` display: flex; align-items: center; gap: 4px; `;
const LegendDot = styled.span` width: 12px; height: 12px; border-radius: 2px; display: inline-block; `;

const Body = styled.div` flex: 1; display: flex; overflow: hidden; `;

const Sidebar = styled.div`
    width: ${ROW_LABEL_W}px; flex-shrink: 0; border-right: 2px solid ${C.border};
    background: ${C.surface}; overflow-y: auto;
`;
const SidebarHeader = styled.div`
    padding: 8px 12px; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em;
    color: ${C.textSecondary}; background: ${C.sidebarBg}; border-bottom: 1px solid ${C.border};
    position: sticky; top: 0; z-index: 5;
`;
const UserRow = styled.div`
    padding: 4px 12px; display: flex; align-items: center; gap: 8px;
    border-bottom: 1px solid ${C.border}; cursor: pointer;
    &:hover { background: ${C.bg}; }
`;
const Avatar = styled.span`
    width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
    color: #fff; font-size: 10px; font-weight: 600; flex-shrink: 0;
`;
const DevInfo = styled.div` display: flex; flex-direction: column; overflow: hidden; `;
const DevName = styled.span`
    font-size: 12px; font-weight: 600; color: ${C.textPrimary}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const DevHours = styled.span` font-size: 10px; color: ${C.textMuted}; `;

const TimelineArea = styled.div` flex: 1; display: flex; flex-direction: column; overflow: hidden; `;
const TimelineScroll = styled.div` flex: 1; overflow: auto; `;
const DateHeader = styled.div`
    display: flex; position: sticky; top: 0; z-index: 10; background: ${C.surface}; border-bottom: 2px solid ${C.border};
`;
const DateHeaderLabel = styled.div`
    width: ${ROW_LABEL_W}px; flex-shrink: 0; border-right: 2px solid ${C.border};
`;
const DateCell = styled.div<{ weekend?: boolean; today?: boolean }>`
    width: ${DAY_WIDTH}px; flex-shrink: 0; text-align: center; border-right: 1px solid ${C.border};
    display: flex; flex-direction: column; justify-content: center; padding: 4px 0;
    background: ${props => { if (props.today) return C.accentLight; return props.weekend ? C.weekend : C.surface; }};
    font-size: 11px; color: ${props => props.today ? C.accent : C.textSecondary};
`;
const DateNum = styled.span<{ today?: boolean }>`
    font-weight: ${props => props.today ? '700' : '400'}; color: ${props => props.today ? C.accent : C.textPrimary}; font-size: 13px;
`;
const GridRow = styled.div`
    display: flex; position: relative; border-bottom: 1px solid ${C.border}; min-width: fit-content;
    &:hover { background: ${C.bg}; }
`;
const RowLabel = styled.div`
    width: ${ROW_LABEL_W}px; flex-shrink: 0; padding: 3px 12px; border-right: 2px solid ${C.border};
    background: ${C.surface}; display: flex; align-items: center; gap: 8px; cursor: pointer; z-index: 5; position: sticky; left: 0;
    &:hover { background: ${C.accentLight}; }
`;
const GridCell = styled.div<{ status: string }>`
    width: ${DAY_WIDTH}px; flex-shrink: 0; border-right: 1px solid ${C.border}; align-self: stretch;
    background: ${props => props.status === 'weekend' ? C.weekend : 'transparent'};
`;
const TimeBlock = styled.div`
    position: absolute; border-radius: 3px; cursor: pointer;
    display: flex; flex-direction: column; justify-content: center; padding: 0 4px;
    font-size: 10px; font-weight: 500; color: #fff; overflow: hidden;
    box-shadow: 0 1px 3px rgba(0,0,0,0.12); transition: all 0.15s;
    &:hover { transform: translateY(-1px); box-shadow: 0 3px 6px rgba(0,0,0,0.25); z-index: 10; }
`;
const TimeBlockTitle = styled.span`
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; line-height: 1.2;
`;
const TimeBlockHours = styled.span`
    background: rgba(0,0,0,0.2); border-radius: 2px; padding: 0 3px; font-size: 9px; align-self: flex-start; margin-top: 2px;
`;
const TodayLine = styled.div`
    position: absolute; top: 0; bottom: 0; width: 2px; background: ${C.todayLine}; z-index: 5; pointer-events: none;
`;
const NoDataMsg = styled.div` padding: 40px; text-align: center; color: ${C.textMuted}; `;

// ── Detail tooltip ──
const Tooltip = styled.div`
    position: fixed; z-index: 100; background: ${C.surface}; border: 1px solid ${C.borderStrong};
    border-radius: 6px; padding: 8px 12px; font-size: 12px; color: ${C.textPrimary};
    box-shadow: 0 4px 12px rgba(0,0,0,0.15); max-width: 300px; pointer-events: none;
`;
const TooltipRow = styled.div` margin-bottom: 2px; `;
const TooltipLabel = styled.span` color: ${C.textMuted}; font-weight: 500; `;
const TooltipValue = styled.span` color: ${C.textPrimary}; `;
const TooltipDesc = styled.div`
    margin-top: 4px; padding-top: 4px; border-top: 1px solid ${C.border};
    color: ${C.textSecondary}; font-style: italic; white-space: pre-wrap;
`;

interface MergedBlock {
    date: Date;
    startMinutes: number;
    endMinutes: number;
    hours: number;
    projectName: string;
    ticketNumbers: string;
    userName: string;
    userId: number;
    regIds: number[];
    description: string;
}

@observer
export default class TimeRegistrationHistory extends AdminOverview {
    myFilterKey = 'planning-time-history';

    store = new TimeRegistrationStore({ relations: ['user', 'projects', 'tickets'], limit: false });
    ticketStore = new TicketStore();

    @observable title = t('nav.planning.timeHistory', 'Time History');
    @observable weekOffset = 0;
    @observable startDate = getStartOfWeek(new Date());
    @observable visibleWeeks = INITIAL_WEEKS;
    @observable selectedUserId: number | null = null;
    @observable hoveredBlock: { block: MergedBlock; x: number; y: number } | null = null;
    @observable loading = false;
    @observable jumpToDate = '';

    // Default params — date range scoped to visible weeks, no unlimited fetch
    params = {
        '.ended_at:isnull': 'false',
        '.user.external': 'false',
        order_by: '-started_at,-ended_at',
    };

    // Filters matching the existing TimeRegistration overview pattern
    filters = [
        {
            type: 'text',
            label: t('timeRegistration.field.description.label', 'Description'),
            name: '.description:icontains',
        },
        {
            type: 'select',
            multiple: true,
            remote: true,
            label: t('timeRegistration.field.projects.label', 'Projects'),
            name: '.projects:in',
            store: new ProjectStore(),
            toOption: (project: any) => ({ value: project.id, text: project.fullName }),
        },
        {
            type: 'select',
            multiple: true,
            remote: true,
            label: t('timeRegistration.field.tickets.label', 'Tickets'),
            name: '.tickets:in',
            store: this.ticketStore,
            toOption: (ticket: any) => ({ value: ticket.id, text: ticket.fullName, number: ticket.number }),
            renderLabel: ({ number, ...props }: any) => {
                props.content = <TicketLabel number={number} />;
                return <Label {...props} />;
            },
        },
        {
            type: 'select',
            multiple: true,
            remote: true,
            label: t('timeRegistration.field.user.label', 'User'),
            name: '.user:in',
            store: new UserStore(),
            toOption: (user: any) => ({ value: user.id, text: user.fullName }),
        },
        {
            type: 'select',
            multiple: true,
            remote: true,
            label: t('timeRegistration.field.projectTeam.label', 'Project team'),
            name: '.projects.team:in',
            store: new TeamStore(),
            toOption: (team: any) => ({ value: team.id, text: team.name }),
        },
        {
            type: 'select',
            multiple: true,
            remote: true,
            label: t('timeRegistration.field.userTeam.label', 'User team'),
            name: '.user.team:in',
            store: new TeamStore(),
            toOption: (team: any) => ({ value: team.id, text: team.name }),
        },
        {
            type: 'radioButtons',
            label: t('user.field.external.label', 'External'),
            name: '.user.external',
            options: [
                { value: 'true', text: t('form.yes', 'Yes') },
                { value: undefined, text: t('form.either', 'Either') },
                { value: 'false', text: t('form.no', 'No') },
            ],
        },
        {
            type: 'custom',
            callback: (props: any) => <FilterTargetSmartDatePicker {...props} store={this.store} afterChange={this.debouncedFetch} />,
            label: t('timeRegistration.field.date.label', 'Date'),
            name: '.started_at:range',
        },
    ];

    get visibleStart(): Date { return this.startDate; }
    get visibleEnd(): Date { return addDays(this.startDate, this.visibleWeeks * 7 - 1); }

    fetchOnMount() {
        return true;
    }

    @action prevWeek = () => {
        this.weekOffset--;
        this.startDate = addDays(getStartOfWeek(new Date()), this.weekOffset * 7);
        this.visibleWeeks = INITIAL_WEEKS;
        this.updateDateRangeAndFetch();
    };
    @action nextWeek = () => {
        this.weekOffset++;
        this.startDate = addDays(getStartOfWeek(new Date()), this.weekOffset * 7);
        this.visibleWeeks = INITIAL_WEEKS;
        this.updateDateRangeAndFetch();
    };
    @action goToCurrentWeek = () => {
        this.weekOffset = 0;
        this.startDate = getStartOfWeek(new Date());
        this.visibleWeeks = INITIAL_WEEKS;
        this.updateDateRangeAndFetch();
    };

    @action updateDateRangeAndFetch() {
        const startStr = moment(this.visibleStart).format('YYYY-MM-DD');
        const endStr = moment(this.visibleEnd).format('YYYY-MM-DD');
        this.store.params['.started_at:range'] = `${startStr},${endStr}`;
        this.fetch();
    }

// Ensure initial date range is set before first fetch
    componentDidMount() {
        const startStr = moment(this.visibleStart).format('YYYY-MM-DD');
        const endStr = moment(this.visibleEnd).format('YYYY-MM-DD');
        this.store.params['.started_at:range'] = `${startStr},${endStr}`;
        // Let AdminOverview's componentDidMount handle bindUrlParams + fetch
        // @ts-ignore
        super.componentDidMount();
        // bindUrlParams restores limit=25 from the URL (PAGINATION_KEYS), overriding
        // the limit:false set in the constructor. Force it back to no limit.
        this.store.setLimit(false);
    }

    get users(): User[] {
        // Derive users from the loaded time registrations (user relation is already fetched)
        const userMap = new Map<number, User>();
        for (const tr of this.store.models as TimeRegistration[]) {
            const u = (tr as any).user;
            if (u && typeof u === 'object' && u.id) {
                userMap.set(Number(u.id), u);
            }
        }
        return Array.from(userMap.values());
    }

    get timeRegistrations(): TimeRegistration[] {
        return (this.store.models as TimeRegistration[]) || [];
    }

    getUserRegs(userId: number): TimeRegistration[] {
        return this.timeRegistrations.filter((tr: any) => {
            const uid = tr.user?.id ?? tr.user;
            return Number(uid) === Number(userId);
        });
    }

    // Split a time registration into per-day intervals (for multi-day regs)
    getDayIntervals(tr: TimeRegistration): { date: Date; startMinutes: number; endMinutes: number; hours: number }[] {
        const startedAt = parseDateSafe((tr as any).startedAt);
        const endedAt = parseDateSafe((tr as any).endedAt);
        if (!startedAt || !endedAt) return [];

        const startDay = new Date(startedAt); startDay.setHours(0,0,0,0);
        const endDay = new Date(endedAt); endDay.setHours(0,0,0,0);
        const result: { date: Date; startMinutes: number; endMinutes: number; hours: number }[] = [];

        let currentDay = new Date(startDay);
        while (currentDay <= endDay) {
            const dayStart = new Date(currentDay);
            dayStart.setHours(startedAt.getHours(), startedAt.getMinutes(), 0, 0);
            const dayEnd = new Date(currentDay);
            if (currentDay.toDateString() === endDay.toDateString()) {
                dayEnd.setHours(endedAt.getHours(), endedAt.getMinutes(), 0, 0);
            } else {
                dayEnd.setHours(23, 59, 0, 0);
            }
            if (currentDay.toDateString() === startDay.toDateString() && currentDay.toDateString() !== endDay.toDateString()) {
                dayStart.setHours(startedAt.getHours(), startedAt.getMinutes(), 0, 0);
                dayEnd.setHours(23, 59, 0, 0);
            }
            const hours = (dayEnd.getTime() - dayStart.getTime()) / (1000 * 3600);
            if (hours > 0) {
                result.push({
                    date: new Date(currentDay),
                    startMinutes: dayStart.getHours() * 60 + dayStart.getMinutes(),
                    endMinutes: dayEnd.getHours() * 60 + dayEnd.getMinutes(),
                    hours,
                });
            }
            currentDay = addDays(currentDay, 1);
        }
        return result;
    }

    // Merge overlapping/adjacent intervals (interval merging algorithm)
    mergeIntervals(intervals: { startMinutes: number; endMinutes: number }[]): { startMinutes: number; endMinutes: number; hours: number }[] {
        if (intervals.length === 0) return [];
        const sorted = [...intervals].sort((a, b) => a.startMinutes - b.startMinutes);
        const merged: { startMinutes: number; endMinutes: number; hours: number }[] = [];
        let current = { ...sorted[0] };
        for (let i = 1; i < sorted.length; i++) {
            if (sorted[i].startMinutes <= current.endMinutes) {
                current.endMinutes = Math.max(current.endMinutes, sorted[i].endMinutes);
            } else {
                merged.push({ ...current, hours: (current.endMinutes - current.startMinutes) / 60 });
                current = { ...sorted[i] };
            }
        }
        merged.push({ ...current, hours: (current.endMinutes - current.startMinutes) / 60 });
        return merged;
    }

    // Group time registrations by project within each day, merging overlapping intervals
    // This collapses many small entries (e.g. every 15 min) into one block per project per day
    getMergedBlocks(userId: number): MergedBlock[] {
        const regs = this.getUserRegs(userId);
        const blocks: MergedBlock[] = [];

        // Group day-intervals by date + project key
        // key = dateStr|projectName
        type PendingEntry = {
            date: Date;
            startMinutes: number;
            endMinutes: number;
            projectName: string;
            ticketNumbers: string;
            description: string;
            regId: number;
        };
        const byDayProject = new Map<string, PendingEntry[]>();

        for (const tr of regs) {
            const intervals = this.getDayIntervals(tr);
            const projects = ((tr as any).projects?.models || (tr as any).projects || []);
            const projectName = projects.length > 0 ? (projects[0].name || projects[0].fullName || 'Unknown') : 'No project';
            const ticketNumbers = ((tr as any).tickets?.models || (tr as any).tickets || [])
                .map((tk: any) => 'T' + tk.number).filter(Boolean).join(', ');
            const description = (tr as any).description || '';
            const uAny = (tr as any).user;
            const userName = uAny?.fullName || ((uAny?.firstName || '') + ' ' + (uAny?.lastName || '').trim()).trim() || 'Unknown';
            const uid = Number(uAny?.id ?? 0);

            for (const interval of intervals) {
                const key = interval.date.toDateString() + '|' + projectName;
                if (!byDayProject.has(key)) byDayProject.set(key, []);
                byDayProject.get(key) && byDayProject.get(key).push({
                    date: interval.date,
                    startMinutes: interval.startMinutes,
                    endMinutes: interval.endMinutes,
                    projectName,
                    ticketNumbers,
                    description,
                    regId: (tr as any).id,
                });
            }

            // Store user info on the reg for later use
            (tr as any)._userName = userName;
            (tr as any)._userId = uid;
        }

        // For each day+project group, merge intervals and create blocks
        for (const [, entries] of byDayProject) {
            const merged = this.mergeIntervals(entries.map(e => ({ startMinutes: e.startMinutes, endMinutes: e.endMinutes })));
            const first = entries[0];
            // Find user info from the first reg
            const sampleReg = regs.find((r: any) => r.id === first.regId) as any;
            const userName = sampleReg?._userName || 'Unknown';
            const uid = sampleReg?._userId || 0;

            // Collect all ticket numbers and descriptions
            const allTickets = new Set<string>();
            const allDescs: string[] = [];
            const allRegIds: number[] = [];
            for (const e of entries) {
                if (e.ticketNumbers) e.ticketNumbers.split(', ').forEach((t: string) => allTickets.add(t));
                if (e.description) allDescs.push(e.description);
                allRegIds.push(e.regId);
            }

            for (const m of merged) {
                blocks.push({
                    date: new Date(first.date),
                    startMinutes: m.startMinutes,
                    endMinutes: m.endMinutes,
                    hours: m.hours,
                    projectName: first.projectName,
                    ticketNumbers: Array.from(allTickets).join(', '),
                    userName,
                    userId: uid,
                    regIds: allRegIds,
                    description: allDescs.filter(Boolean).join(' / '),
                });
            }
        }

        return blocks;
    }

    // Get total hours for a user in the visible period
    getUserTotalHours(userId: number): number {
        const blocks = this.getMergedBlocks(userId);
        let totalSeconds = 0;
        for (const b of blocks) {
            totalSeconds += b.hours * 3600;
        }
        return totalSeconds;
    }

    getTimelineDates(): Date[] {
        const dates: Date[] = [];
        for (let i = 0; i < this.visibleWeeks * 7; i++) dates.push(addDays(this.startDate, i));
        return dates;
    }

    get currentWeekLabel(): string {
        const end = addDays(this.startDate, this.visibleWeeks * 7 - 1);
        return this.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            + ' \u2013 ' + end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    getBlocksForDate(userId: number, date: Date): MergedBlock[] {
        const blocks = this.getMergedBlocks(userId);
        return blocks.filter(b => b.date.toDateString() === date.toDateString());
    }

    // Per-day horizontal lane assignment for overlapping merged blocks
    getDayLanes(userId: number): Map<string, Map<string, { lane: number; totalLanes: number }>> {
        const blocks = this.getMergedBlocks(userId);
        const byDate = new Map<string, MergedBlock[]>();
        for (const b of blocks) {
            const key = b.date.toDateString();
            if (!byDate.has(key)) byDate.set(key, []);
            const arr = byDate.get(key); if (arr) { arr.push(b); }
        }

        const result = new Map<string, Map<string, { lane: number; totalLanes: number }>>();
        for (const [dateStr, dayBlocks] of byDate) {
            const sorted = [...dayBlocks].sort((a, b) => a.startMinutes - b.startMinutes);
            const lanes: { endMin: number }[] = [];
            const blockLanes = new Map<string, { lane: number; totalLanes: number }>();

            for (const b of sorted) {
                const blockKey = b.projectName + '|' + b.startMinutes;
                let lane = 0;
                while (lane < lanes.length && lanes[lane].endMin > b.startMinutes) {
                    lane++;
                }
                if (lane >= lanes.length) lanes.push({ endMin: b.endMinutes });
                else lanes[lane].endMin = b.endMinutes;
                blockLanes.set(blockKey, { lane, totalLanes: 0 });
            }

            const totalLanes = lanes.length;
            for (const [, val] of blockLanes) {
                val.totalLanes = totalLanes;
            }

            result.set(dateStr, blockLanes);
        }
        return result;
    }

    // Get the earliest start time for a specific day (per-day offset, not per-user)
    getDayEarliestStartMinutes(userId: number, dateStr: string): number {
        const blocks = this.getMergedBlocks(userId);
        const dayBlocks = blocks.filter(b => b.date.toDateString() === dateStr);
        if (dayBlocks.length === 0) return 0;
        let earliest = 24 * 60;
        for (const b of dayBlocks) {
            earliest = Math.min(earliest, b.startMinutes);
        }
        return earliest;
    }

    // Get the max bottom pixel for a user across all days (using per-day offsets)
    getMaxBlockBottom(userId: number): number {
        const blocks = this.getMergedBlocks(userId);
        if (blocks.length === 0) return 0;
        // Group by date to get per-day offsets
        const byDate = new Map<string, MergedBlock[]>();
        for (const b of blocks) {
            const key = b.date.toDateString();
            if (!byDate.has(key)) byDate.set(key, []);
            const arr = byDate.get(key); if (arr) { arr.push(b); }
        }
        let maxBottom = 0;
        for (const [, dayBlocks] of byDate) {
            const offsetMin = dayBlocks.reduce((min, b) => Math.min(min, b.startMinutes), 24 * 60);
            for (const b of dayBlocks) {
                const adjustedTop = b.startMinutes - offsetMin;
                const top = (adjustedTop / 60) * PX_PER_HOUR;
                const height = b.hours * PX_PER_HOUR;
                maxBottom = Math.max(maxBottom, top + height);
            }
        }
        return maxBottom;
    }

    getBlockColor(block: MergedBlock): string {
        return colorFor(block.projectName);
    }

    @action selectUser = (userId: number) => {
        this.selectedUserId = this.selectedUserId === userId ? null : userId;
    };
    @action handleJumpToDate = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.jumpToDate = e.target.value;
    };
    @action doJumpToDate = () => {
        if (!this.jumpToDate) return;
        const d = new Date(this.jumpToDate);
        if (isNaN(d.getTime())) return;
        this.startDate = getStartOfWeek(d);
        this.weekOffset = Math.round(daysBetween(getStartOfWeek(new Date()), this.startDate) / 7);
        this.visibleWeeks = INITIAL_WEEKS;
        this.updateDateRangeAndFetch();
    };

    @action showTooltip = (block: MergedBlock, e: React.MouseEvent) => {
        this.hoveredBlock = { block, x: e.clientX, y: e.clientY };
    };
    @action hideTooltip = () => {
        this.hoveredBlock = null;
    };

    get filteredUsers(): User[] {
        let result = this.users;
        if (this.selectedUserId !== null) {
            result = result.filter(u => Number(u.id) === this.selectedUserId);
        }
        return result;
    }

    renderContent() {
        const dates = this.getTimelineDates();
        const users = this.filteredUsers;

        return (
            <>
            <Wrapper>
                <Toolbar>
                    <TBtn data-test-timeline-prev-week onClick={this.prevWeek}>{'\u2039'}</TBtn>
                    <TBtn data-test-timeline-today onClick={this.goToCurrentWeek}>Today</TBtn>
                    <TBtn data-test-timeline-next-week onClick={this.nextWeek}>{'\u203A'}</TBtn>
                    <WeekLabel>{this.currentWeekLabel}</WeekLabel>
                    <span style={{ width: 1, background: C.border, height: 24, margin: '0 4px' }} />
                    {this.selectedUserId !== null && (
                        <TBtnActive data-test-show-all-users onClick={() => { this.selectedUserId = null; }}>
                            Show All Users
                        </TBtnActive>
                    )}
                    <span style={{ width: 1, background: C.border, height: 24, margin: '0 4px' }} />
                    <span style={{ fontSize: 12, color: C.textSecondary, fontWeight: 600 }}>Jump to:</span>
                    <input
                        type="date"
                        data-test-jump-to-date
                        value={this.jumpToDate}
                        onChange={this.handleJumpToDate}
                        style={{ padding: '4px 8px', border: '1px solid ' + C.borderStrong, borderRadius: 4, fontSize: 13, background: C.surface, color: C.textPrimary }}
                    />
                    <TBtn data-test-jump-to-date-go onClick={this.doJumpToDate}>Go</TBtn>
                    <span style={{ flex: 1 }} />
                    <span style={{ fontSize: 12, color: C.textMuted }}>
                        {this.timeRegistrations.length} registrations
                    </span>
                </Toolbar>

                <LegendBar>
                    <LegendItem>
                        <LegendDot style={{ background: C.accentLight, border: '1px solid ' + C.accent }} />
                        Click a user to filter
                    </LegendItem>
                    <LegendItem>
                        <LegendDot style={{ background: C.weekend, border: '1px solid ' + C.border }} />
                        Weekend
                    </LegendItem>
                    <LegendItem>
                        <span style={{ fontSize: 11 }}>Blocks merged by project per day (10px/hour)</span>
                    </LegendItem>
                </LegendBar>

                <Body>
                    <Sidebar>
                        <SidebarHeader>
                            {this.selectedUserId ? 'Selected Developer' : 'Developers'} ({users.length})
                        </SidebarHeader>
                        {users.map(u => {
                            const uAny = u as any;
                            const uname = uAny.fullName || (uAny.firstName || '') + ' ' + (uAny.lastName || '').trim();
                            const initials = (uAny.firstName || '?').charAt(0) + (uAny.lastName || '').charAt(0);
                            const ucolor = colorFor(uname);
                            const totalHours = this.getUserTotalHours(u.id);
                            const isSelected = this.selectedUserId === u.id;
                            return (
                                <UserRow
                                    key={u.id}
                                    data-test-user-row={u.id}
                                    onClick={() => this.selectUser(u.id)}
                                    style={isSelected ? { background: C.accentLight, borderLeft: '3px solid ' + C.accent } : {}}
                                >
                                    <Avatar style={{ background: ucolor }}>{initials}</Avatar>
                                    <DevInfo>
                                        <DevName>{uname}</DevName>
                                        <DevHours>{formatHours(totalHours)} this period</DevHours>
                                    </DevInfo>
                                </UserRow>
                            );
                        })}
                        {users.length === 0 && (
                            <NoDataMsg>No time registrations in this period.</NoDataMsg>
                        )}
                    </Sidebar>

                    <TimelineArea>
                        <TimelineScroll id="timeline-scroll-area">
                            <DateHeader>
                                <DateHeaderLabel />
                                {dates.map((date, i) => (
                                    <DateCell key={i} weekend={isWeekend(date)} today={isToday(date)}>
                                        <span>{date.toLocaleDateString('en-US', { weekday: 'short' }).charAt(0)}</span>
                                        <DateNum today={isToday(date)}>{date.getDate()}</DateNum>
                                        <span style={{ fontSize: 9, color: C.textMuted }}>
                                            {date.toLocaleDateString('en-US', { month: 'short' })}
                                        </span>
                                    </DateCell>
                                ))}
                            </DateHeader>

                            {users.map(u => {
                                const uAny = u as any;
                                const uname = uAny.fullName || (uAny.firstName || '') + ' ' + (uAny.lastName || '').trim();
                                const initials = (uAny.firstName || '?').charAt(0) + (uAny.lastName || '').charAt(0);
                                const ucolor = colorFor(uname);
                                const dayLanes = this.getDayLanes(u.id);
                                const maxBottom = this.getMaxBlockBottom(u.id);
                                const rowHeight = Math.min(MAX_ROW_HEIGHT, Math.max(32, maxBottom + 4));
                                const dayBlocks = new Map<string, MergedBlock[]>();
                                for (const date of dates) {
                                    const blocks = this.getBlocksForDate(u.id, date);
                                    if (blocks.length > 0) dayBlocks.set(date.toDateString(), blocks);
                                }

                                return (
                                    <GridRow key={u.id} data-test-timeline-row={u.id} style={{ height: rowHeight }}>
                                        <RowLabel data-test-dev-row={u.id} onClick={() => this.selectUser(u.id)}>
                                            <Avatar style={{ background: ucolor }}>{initials}</Avatar>
                                            <DevInfo>
                                                <DevName>{uname}</DevName>
                                                <DevHours>{formatHours(this.getUserTotalHours(u.id))}</DevHours>
                                            </DevInfo>
                                        </RowLabel>
                                        {dates.map((date, i) => {
                                            const status = isWeekend(date) ? 'weekend' : 'normal';
                                            return <GridCell key={i} status={status} />;
                                        })}
                                        {/* Render merged project blocks — 10px per hour, merged by project per day */}
                                        {(() => {
                                            const elements: React.ReactNode[] = [];
                                            for (const [dateStr, blocks] of dayBlocks) {
                                                const lanesForDay = dayLanes.get(dateStr);
                                                for (const block of blocks) {
                                                    const blockKey = block.projectName + '|' + block.startMinutes;
                                                    const laneInfo = lanesForDay?.get(blockKey) ?? { lane: 0, totalLanes: 1 };
                                                    const { lane, totalLanes } = laneInfo;
                                                    const dayOffset = daysBetween(this.startDate, block.date);
                                                    const laneWidth = DAY_WIDTH / totalLanes;
                                                    const left = dayOffset * DAY_WIDTH + lane * laneWidth + 1;
                                                    const width = laneWidth - 2;
                                                    const offsetMin = this.getDayEarliestStartMinutes(u.id, dateStr);
                                            const top = ((block.startMinutes - offsetMin) / 60) * PX_PER_HOUR;
                                                    const height = Math.max(2, block.hours * PX_PER_HOUR);
                                                    const projColor = this.getBlockColor(block);
                                                    const title = block.projectName || block.ticketNumbers || 'Time registration';
                                                    elements.push(
                                                        <TimeBlock
                                                            key={block.projectName + '-' + dateStr + '-' + block.startMinutes}
                                                            data-test-time-block={block.regIds[0]}
                                                            style={{
                                                                left: left + ROW_LABEL_W,
                                                                width,
                                                                top,
                                                                height,
                                                                background: projColor,
                                                            }}
                                                            onMouseEnter={(e) => this.showTooltip(block, e)}
                                                            onMouseLeave={this.hideTooltip}
                                                            onMouseMove={(e) => { if (this.hoveredBlock) { this.hoveredBlock = { block, x: e.clientX, y: e.clientY }; } }}
                                                        >
                                                            {height >= 14 && <TimeBlockTitle>{title}</TimeBlockTitle>}
                                                            {height >= 24 && block.hours >= 1 && (
                                                                <TimeBlockHours>{block.hours.toFixed(1)}h</TimeBlockHours>
                                                            )}
                                                        </TimeBlock>
                                                    );
                                                }
                                            }
                                            return elements;
                                        })()}
                                        {dates.some(isToday) && (
                                            <TodayLine style={{ left: daysBetween(this.startDate, new Date()) * DAY_WIDTH + ROW_LABEL_W }} />
                                        )}
                                    </GridRow>
                                );
                            })}

                            {users.length === 0 && (
                                <NoDataMsg>
                                    {this.loading ? 'Loading...' : 'No time registrations found in this period. Use \u2190 \u2192 to navigate through time.'}
                                </NoDataMsg>
                            )}
                        </TimelineScroll>
                    </TimelineArea>
                </Body>

                {/* Hover tooltip */}
                {this.hoveredBlock && (() => {
                    const b = this.hoveredBlock.block;
                    const startH = Math.floor(b.startMinutes / 60);
                    const startM = b.startMinutes % 60;
                    const endH = Math.floor(b.endMinutes / 60);
                    const endM = b.endMinutes % 60;
                    const timeStr = (h: number, m: number) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                    return (
                        <Tooltip
                            data-test-time-tooltip
                            style={{
                                left: Math.min(this.hoveredBlock.x + 12, window.innerWidth - 320),
                                top: this.hoveredBlock.y + 12,
                            }}
                        >
                            <TooltipRow>
                                <TooltipLabel>User: </TooltipLabel>
                                <TooltipValue>{b.userName}</TooltipValue>
                            </TooltipRow>
                            <TooltipRow>
                                <TooltipLabel>Project: </TooltipLabel>
                                <TooltipValue>{b.projectName}</TooltipValue>
                            </TooltipRow>
                            <TooltipRow>
                                <TooltipLabel>Time: </TooltipLabel>
                                <TooltipValue>
                                    {timeStr(startH, startM)} {'\u2013'} {timeStr(endH, endM)}
                                </TooltipValue>
                            </TooltipRow>
                            <TooltipRow>
                                <TooltipLabel>Duration: </TooltipLabel>
                                <TooltipValue>{b.hours.toFixed(2)}h ({b.regIds.length} entries merged)</TooltipValue>
                            </TooltipRow>
                            {b.ticketNumbers && (
                                <TooltipRow>
                                    <TooltipLabel>Tickets: </TooltipLabel>
                                    <TooltipValue>{b.ticketNumbers}</TooltipValue>
                                </TooltipRow>
                            )}
                            {b.description && (
                                <TooltipDesc>{b.description}</TooltipDesc>
                            )}
                        </Tooltip>
                    );
                })()}
            </Wrapper>
            {this.renderSidebars()}
            </>
        );
    }
}
